import { memo, useEffect, useState } from 'react';
import { assetsOrgAssetsList, type OrgAssetResponse } from '@goldcast/api/content';
import VideoTabContent from './VideoTabContent';
import ImageTabContent from './ImageTabContent';
import Icon from '@/components/atoms/Icon';
import Button from '@/components/atoms/Button/Button';
import IconButton from '@/components/atoms/Button/IconButton';

type OverlayTab = 'video' | 'image';

interface ClipCustomizerOverlaysPopupProps {
  closePopup: () => void;
}

const ClipCustomizerOverlaysPopup = ({ closePopup }: ClipCustomizerOverlaysPopupProps) => {
  const [activeTab, setActiveTab] = useState<OverlayTab>('video');
  const [recentVideoAssets, setRecentVideoAssets] = useState<OrgAssetResponse[]>([]);
  const [recentImageAssets, setRecentImageAssets] = useState<OrgAssetResponse[]>([]);

  useEffect(() => {
    Promise.all([
      assetsOrgAssetsList({ queryParams: { type: 'VIDEO', limit: 6, ordering: '-created_at' } }),
      assetsOrgAssetsList({ queryParams: { type: 'IMAGE', limit: 6, ordering: '-created_at' } })
    ])
      .then(([videoRes, imageRes]) => {
        setRecentVideoAssets(videoRes.results);
        setRecentImageAssets(imageRes.results);
      })
      .catch(() => {});
  }, []);

  return (
    <div className="flex h-[calc(100vh-120px)] w-[480px] flex-col overflow-y-auto rounded-[14px] border bg-white shadow">
      <div className="sticky top-0 z-10 bg-white px-4 pt-4">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-base font-semibold -tracking-normal text-black">Overlays</h2>
          <IconButton
            icon="IconX"
            trackingId="overlay-popup-close"
            onClick={closePopup}
            variation="none"
            title="Close overlays"
            buttonClassName="rounded p-0.5 text-black hover:bg-slate-100 hover:text-gray-800"
          />
        </div>
        <div className="flex border-b border-slate-200">
          <Button
            trackingId="overlay-tab-video"
            onClick={() => setActiveTab('video')}
            variation="none"
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-none pb-2 text-sm font-medium transition-colors ${
              activeTab === 'video' ? 'border-b-2 border-gray-900 text-gray-900' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Icon name="IconMovie" className="size-4" />
            Video
          </Button>
          <Button
            trackingId="overlay-tab-image"
            onClick={() => setActiveTab('image')}
            variation="none"
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-none pb-2 text-sm font-medium transition-colors ${
              activeTab === 'image' ? 'border-b-2 border-gray-900 text-gray-900' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Icon name="IconPhoto" className="size-4" />
            Image
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-5 p-4">
        {activeTab === 'video' && <VideoTabContent closePopup={closePopup} recentAssets={recentVideoAssets} />}
        {activeTab === 'image' && <ImageTabContent closePopup={closePopup} recentAssets={recentImageAssets} />}
      </div>
    </div>
  );
};

export default memo(ClipCustomizerOverlaysPopup);
